import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import type { MouseEventHandler, PropsWithChildren, ReactNode, Ref } from 'react';
import { FileFormatBadge, type DropdownOptionBadgePreset } from './file-format-badge';

export type DropdownOptionIconPreset = 'download' | 'upload';
export type DropdownOptionIcon = DropdownOptionIconPreset | ReactNode;
export type DropdownOptionBadge = DropdownOptionBadgePreset | ReactNode;

export type Props = PropsWithChildren<{
  ref?: Ref<HTMLButtonElement>;
  icon?: DropdownOptionIcon;
  badge?: DropdownOptionBadge;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  ariaLabel?: string;
  tid?: string;
}>;

function resolveIcon(icon: DropdownOptionIcon): ReactNode {
  if (icon === 'download') return <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />;
  if (icon === 'upload') return <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />;

  return icon;
}

function isBadgePreset(badge: DropdownOptionBadge): badge is DropdownOptionBadgePreset {
  return typeof badge === 'string' && (badge === 'excel' || badge === 'pdf');
}

function resolveBadge(badge: DropdownOptionBadge): ReactNode {
  if (isBadgePreset(badge)) return <FileFormatBadge format={badge} />;

  return badge;
}

export function option(props: Props) {
  const { ref, children, icon, badge, onClick, disabled, ariaLabel, tid } = props;
  const hasIcon = icon !== undefined && icon !== null;
  const hasBadge = badge !== undefined && badge !== null;

  return (
    <MuiButton
      ref={ref}
      type="button"
      role="menuitem"
      aria-label={ariaLabel}
      title={typeof children === 'string' ? children : undefined}
      onClick={onClick}
      disabled={disabled}
      data-testid={tid}
      sx={{
        display: 'flex',
        width: '100%',
        minWidth: 0,
        minHeight: 44,
        justifyContent: 'flex-start',
        px: 2,
        py: 0.875,
        borderRadius: 0,
        color: '#22A7F0',
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.4,
        textTransform: 'none',
        textAlign: 'left',
        overflow: 'hidden',
        transition: 'background-color 140ms ease, box-shadow 140ms ease',
        '&:hover': {
          backgroundColor: 'rgba(34, 167, 240, 0.08)',
          boxShadow: 'inset 3px 0 0 rgba(34, 167, 240, 0.55)',
        },
        '&:active': {
          backgroundColor: 'rgba(34, 167, 240, 0.13)',
        },
        '&.Mui-focusVisible': {
          backgroundColor: 'rgba(34, 167, 240, 0.08)',
          boxShadow: 'inset 0 0 0 2px rgba(34, 167, 240, 0.52)',
        },
        '&:hover .SiloReal-Dropdown-Option-icon': {
          borderColor: 'rgba(34, 167, 240, 0.28)',
          backgroundColor: 'rgba(34, 167, 240, 0.14)',
        },
        '&.Mui-disabled': {
          color: '#9e9e9e',
          boxShadow: 'none',
        },
        '&.Mui-disabled .SiloReal-Dropdown-Option-icon': {
          borderColor: 'rgba(158, 158, 158, 0.18)',
          backgroundColor: 'rgba(158, 158, 158, 0.08)',
        },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'flex',
          width: '100%',
          minWidth: 0,
          alignItems: 'center',
        }}
      >
        {hasIcon ? (
          <Box
            component="span"
            className="SiloReal-Dropdown-Option-icon"
            aria-hidden
            sx={{
              display: 'inline-flex',
              width: 28,
              height: 28,
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.25,
              border: '1px solid rgba(34, 167, 240, 0.18)',
              borderRadius: '6px',
              color: 'inherit',
              backgroundColor: 'rgba(34, 167, 240, 0.08)',
              lineHeight: 0,
              transition: 'border-color 140ms ease, background-color 140ms ease',
              '& .MuiSvgIcon-root': {
                color: 'inherit',
              },
            }}
          >
            {resolveIcon(icon)}
          </Box>
        ) : null}

        <Box
          component="span"
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {children}
        </Box>

        {hasBadge ? (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              flexShrink: 0,
              alignItems: 'center',
              ml: 2,
            }}
          >
            {resolveBadge(badge)}
          </Box>
        ) : null}
      </Box>
    </MuiButton>
  );
}

option.displayName = 'SiloReal-Dropdown-Option';
